/**
 * The platform's CSV of a run-wide list, read back into the rows of the list (ADR 9069, platform ADR 6194 section 6).
 *
 * The run-wide doors and lanes lists draw the first fifty rows and say how many the filters match. Their file is every one of
 * them, and it goes through the SAME export path as every other table: the headers come from the table's column catalogue, the
 * workbook gains its «Columns» sheet, the provenance is the product's, and the CSV and the Excel are built by `src/export`.
 * What the platform contributes is the ROWS, in ONE streamed request (`GET /exit/consolidation/doors|lines/export` with
 * `format=csv`): no cursor, no page size, no ceiling in the browser. This module turns that CSV into the page's own row type, so
 * the one builder per table (`./exports.ts`) takes them exactly as it takes a page's items.
 *
 * **The CSV is the page's, by construction.** The platform writes a column for every field of a page's item, named by the field,
 * so the mapping is by NAME and a field the page gains is read when this table names it. A required field the file does not
 * carry is a refusal (`RunListFileError`), never a row with a guessed value; a column this version does not know is ignored.
 *
 * **Values are the page's.** A number is a number, `true`/`false` a boolean, an empty cell an absent value (`null`) — never a
 * zero, never an empty string standing for unknown. A decimal the page carries as a string stays a string, digit for digit. A
 * text cell the platform neutralised against spreadsheet formulas (a leading apostrophe on `=`, `+`, `-`, `@`, tab, return) is
 * restored: the export builder neutralises it again on the way out, once.
 */

import type { ConsolidationDoorSummary, ConsolidationLineSummary } from '@/api/generated';

/** The file does not state the rows of the list: a required column is missing, or a cell is not the type its column holds. */
export class RunListFileError extends Error {
  constructor(public readonly detail: string) {
    super(`The platform's file of the list cannot be read: ${detail}.`);
    this.name = 'RunListFileError';
  }
}

/**
 * How a field is read. `text`: a string, empty when the cell is empty. `nullable`: a string, `null` when the cell is empty.
 * `integer`, `boolean`: required. `optionalInteger`: `null` when empty. An absent `nullable` or `optional…` COLUMN is allowed
 * (the field is simply absent); an absent required column is a refusal.
 */
type FieldKind = 'text' | 'nullable' | 'integer' | 'optionalInteger' | 'boolean';

/** The kinds whose COLUMN may be absent from a file (the field is then absent), unlike the three amounts below. */
const OPTIONAL_KINDS: readonly FieldKind[] = ['nullable', 'optionalInteger'];

/** The door position's fields, each named once: a field the page gains is a compile error here until it is read. */
const DOOR_FIELDS: { readonly [K in keyof ConsolidationDoorSummary]-?: FieldKind } = {
  as_of: 'text', cluster_id: 'nullable', country_key: 'text', longest_contiguous_run: 'integer', node_role: 'text',
  option_id: 'text', residual_units: 'integer', run_id: 'text', run_is_broken: 'boolean', sell_through_per_capacity: 'nullable',
  sizes_in_stock: 'integer', store_key: 'text', store_name: 'nullable', store_name_absent_reason: 'nullable',
};

/** The lane's fields. The three amounts are required columns whose cell may be empty (`null`: no cost landed, never free). */
const LINE_FIELDS: { readonly [K in keyof ConsolidationLineSummary]-?: FieldKind } = {
  action_id: 'nullable', as_of: 'text', country_key: 'text', currency_code: 'text', destination_store_key: 'text',
  destination_store_name: 'nullable', destination_store_name_absent_reason: 'nullable', elasticity_basis: 'nullable',
  incremental_revenue_amount: 'nullable', is_proposed: 'boolean', lane_cost_amount: 'nullable', line_id: 'text',
  net_benefit_amount: 'nullable', option_id: 'text', reason_code: 'text', repairs_size_run: 'boolean', revenue_basis: 'nullable',
  run_id: 'text', size_absent_reason: 'nullable', size_code: 'nullable', size_key: 'text', size_run_position: 'optionalInteger',
  size_scale: 'nullable', source_store_key: 'text', source_store_name: 'nullable', source_store_name_absent_reason: 'nullable',
  // bata-reqs ADR 9000 (platform #891): absent from an older platform's file, empty for a line that ships as before.
  tranche_week: 'nullable', units: 'integer', velocity_through: 'nullable',
};

/** The amounts of a lane: required columns (the page always states them) that hold `null` where nobody priced the lane. */
const REQUIRED_NULLABLE: ReadonlySet<string> = new Set(['incremental_revenue_amount', 'lane_cost_amount', 'net_benefit_amount']);

/**
 * RFC 4180 as the platform writes it: a field is quoted when it holds a comma, a quote or a line break, the quote doubled; lines end
 * CRLF; the file opens with a byte-order mark for Excel. A final line without a break is read; a blank line is not a row.
 */
export function parseCsv(text: string): string[][] {
  const source = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let started = false;
  const length = source.length;
  let at = 0;
  while (at < length) {
    const char = source[at]!;
    if (quoted) {
      // A run of ordinary characters is taken whole: a file of hundreds of thousands of rows is not read a character at a time.
      const next = source.indexOf('"', at);
      if (next === -1) break;
      field += source.slice(at, next);
      if (source[next + 1] === '"') { field += '"'; at = next + 2; } else { quoted = false; at = next + 1; }
      continue;
    }
    if (char === '"') { quoted = true; started = true; at += 1; continue; }
    if (char === ',') { row.push(field); field = ''; started = true; at += 1; continue; }
    if (char === '\r' || char === '\n') {
      at += char === '\r' && source[at + 1] === '\n' ? 2 : 1;
      if (started || field !== '') { row.push(field); rows.push(row); }
      row = []; field = ''; started = false;
      continue;
    }
    let end = at + 1;
    while (end < length) {
      const code = source.charCodeAt(end);
      if (code === 44 || code === 34 || code === 10 || code === 13) break;
      end += 1;
    }
    field += source.slice(at, end);
    started = true;
    at = end;
  }
  if (quoted) throw new RunListFileError('a quoted cell is never closed');
  if (started || field !== '') { row.push(field); rows.push(row); }
  return rows;
}

/** A text cell with the platform's formula guard taken off: `'=1+1` was written for `=1+1`. */
const FORMULA_GUARD = /^'(?=[=+\-@\t\r])/;

function cellOf(kind: FieldKind, name: string, cell: string, line: number): string | number | boolean | null {
  switch (kind) {
    case 'text': return cell.replace(FORMULA_GUARD, '');
    case 'nullable': return cell === '' ? null : cell.replace(FORMULA_GUARD, '');
    case 'integer':
    case 'optionalInteger': {
      if (cell === '' && kind === 'optionalInteger') return null;
      if (!/^-?\d+$/.test(cell)) throw new RunListFileError(`line ${line}: «${name}» is not a whole number`);
      return Number(cell);
    }
    case 'boolean': {
      if (cell === 'true') return true;
      if (cell === 'false') return false;
      throw new RunListFileError(`line ${line}: «${name}» is neither true nor false`);
    }
    default: { const exhaustive: never = kind; throw new Error(String(exhaustive)); }
  }
}

/** The rows of a list's file, read by field name against `fields`. */
function rowsOf<T>(text: string, fields: Readonly<Record<string, FieldKind>>): T[] {
  const [header, ...body] = parseCsv(text);
  if (header === undefined) throw new RunListFileError('the file is empty');
  const at = new Map(header.map((name, index) => [name, index]));
  const present: (readonly [string, FieldKind, number])[] = [];
  for (const [name, kind] of Object.entries(fields)) {
    const index = at.get(name);
    if (index === undefined) {
      if (OPTIONAL_KINDS.includes(kind) && !REQUIRED_NULLABLE.has(name)) continue;
      throw new RunListFileError(`the column «${name}» is missing`);
    }
    present.push([name, kind, index]);
  }
  return body.map((cells, index) => {
    if (cells.length !== header.length) throw new RunListFileError(`line ${index + 2} has ${cells.length} cells, the header ${header.length}`);
    const item: Record<string, string | number | boolean | null> = {};
    for (const [name, kind, column] of present) item[name] = cellOf(kind, name, cells[column]!, index + 2);
    return item as T;
  });
}

/** The door positions of the platform's CSV of the run-wide doors list. */
export function doorsFromCsv(text: string): ConsolidationDoorSummary[] {
  return rowsOf<ConsolidationDoorSummary>(text, DOOR_FIELDS);
}

/** The lanes of the platform's CSV of the run-wide lanes list. */
export function linesFromCsv(text: string): ConsolidationLineSummary[] {
  return rowsOf<ConsolidationLineSummary>(text, LINE_FIELDS);
}
